import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../services/auth/auth.service';
import { ApiServiceService } from '../../services/common/api-service.service';
import Swal from 'sweetalert2';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  loginForm!: FormGroup;
  forgotPasswordForm!: FormGroup;
  hidePassword = true;
  showForgotPassword = false;
  forgotPasswordStep: 'email' | 'otp' = 'email';
  forgotPasswordLoading = false;
  forgotPasswordMessage = 'Enter your registered email address and we will send you a one-time OTP.';

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private apiService: ApiServiceService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.loginForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required]
    });

    this.forgotPasswordForm = this.formBuilder.group({
      forgotEmail: ['', [Validators.required, Validators.email]],
      forgotOtp: ['', [Validators.required, Validators.minLength(6)]],
      forgotPassword: ['', [Validators.required, Validators.minLength(6)]]
    });
  }

  ngOnInit(): void {
    if (this.authService.isLoggedIn()) {
      this.router.navigate(['/dashboard']);
    }
    this.document.body.classList.add('auth-page-body');
  }

  ngOnDestroy(): void {
    this.document.body.classList.remove('auth-page-body');
  }

  togglePassword(): void {
    this.hidePassword = !this.hidePassword;
  }

  openForgotPassword(): void {
    this.showForgotPassword = true;
    this.forgotPasswordStep = 'email';
    this.forgotPasswordLoading = false;
    this.forgotPasswordMessage = 'Enter your registered email address and we will send you a one-time OTP.';
    this.forgotPasswordForm.reset();
  }

  closeForgotPassword(): void {
    this.showForgotPassword = false;
    this.forgotPasswordStep = 'email';
    this.forgotPasswordLoading = false;
    this.forgotPasswordMessage = 'Enter your registered email address and we will send you a one-time OTP.';
    this.forgotPasswordForm.reset();
  }

  requestForgotPasswordOtp(): void {
    if (this.forgotPasswordForm.get('forgotEmail')?.invalid) {
      this.forgotPasswordForm.get('forgotEmail')?.markAsTouched();
      return;
    }

    this.forgotPasswordLoading = true;
    const email = this.forgotPasswordForm.get('forgotEmail')?.value;

    this.apiService.requestForgotPasswordOtp(email).subscribe({
      next: (response) => {
        this.forgotPasswordLoading = false;
        this.forgotPasswordStep = 'otp';
        this.forgotPasswordMessage = response?.data || 'We have sent a one-time OTP to your email. Enter it below and choose a new password.';
        Swal.fire({
          title: 'OTP Sent',
          text: 'We sent a one-time password to your email. Enter it below to continue.',
          icon: 'success',
          confirmButtonColor: '#a73453'
        });
      },
      error: (error) => {
        this.forgotPasswordLoading = false;
        const message = error?.error?.data || 'Unable to send the reset OTP right now.';
        Swal.fire({ title: 'Reset Failed', text: message, icon: 'error', confirmButtonColor: '#a73453' });
      }
    });
  }

  resetPasswordWithOtp(): void {
    if (this.forgotPasswordForm.get('forgotOtp')?.invalid || this.forgotPasswordForm.get('forgotPassword')?.invalid) {
      this.forgotPasswordForm.get('forgotOtp')?.markAsTouched();
      this.forgotPasswordForm.get('forgotPassword')?.markAsTouched();
      return;
    }

    this.forgotPasswordLoading = true;
    const email = this.forgotPasswordForm.get('forgotEmail')?.value;
    const otp = this.forgotPasswordForm.get('forgotOtp')?.value;
    const password = this.forgotPasswordForm.get('forgotPassword')?.value;

    this.apiService.resetPasswordWithOtp(email, otp, password).subscribe({
      next: (response) => {
        this.forgotPasswordLoading = false;
        Swal.fire({
          title: 'Password Updated',
          text: response?.data || 'Your password has been updated successfully.',
          icon: 'success',
          confirmButtonColor: '#a73453'
        });
        this.closeForgotPassword();
      },
      error: (error) => {
        this.forgotPasswordLoading = false;
        const message = error?.error?.data || 'Unable to reset your password right now.';
        Swal.fire({ title: 'Reset Failed', text: message, icon: 'error', confirmButtonColor: '#a73453' });
      }
    });
  }

  onSubmit(): void {
    if (this.loginForm.valid) {
      const email = this.loginForm.get('email')?.value;
      const password = this.loginForm.get('password')?.value;

      this.authService.login(email, password).subscribe({
        next: (response) => {
          if (response.status === 'success') {
            
            const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/dashboard';
            this.router.navigate([returnUrl]);
          } else {
            const errorText = typeof response.data === 'string' ? response.data : (response.message || 'Invalid username or password.');
            Swal.fire({
              title: 'Login Failed',
              text: errorText,
              icon: 'error',
              confirmButtonColor: '#a73453'
            });
          }
        },
        error: (error) => {
          console.error('Login error:', error);
          const errorText =
            (typeof error?.error?.data === 'string' && error.error.data) ||
            error?.error?.message ||
            (typeof error?.error === 'string' && error.error) ||
            error?.message ||
            'An error occurred during login. Please try again.';
          Swal.fire({
            title: 'Login Error',
            text: errorText,
            icon: 'error',
            confirmButtonColor: '#a73453'
          });
        }
      });
    } else {
      this.loginForm.markAllAsTouched();
    }
  }
}
